import { PlusOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { type SelectProps, ConfigProvider, Divider, Select } from 'antd5';
import { type MouseEvent, type ReactElement, useState } from 'react';

import { Modal } from 'components/ui';
import { useGetCollaboratorsQuery } from 'types/atlas-graphql';
import { notEmpty } from 'utils/types';

import { AddCollabContainer, CollaboratorSelect } from './CollaboratorChooser.style';
import { formatCollaborator, groupCollaborators, sortCollaborators } from './utils';

export interface CollaboratorChooserProps extends SelectProps<string[]> {
  // This prop somehow works but could not be found on downstream interface
  hidden?: boolean;
}

export const CollaboratorChooser = ({
  value,
  defaultValue,
  ...props
}: CollaboratorChooserProps) => {
  const [addModalVisible, setAddModalVisible] = useState(false);

  const { data: collaboratorsData, loading: collaboratorsLoading } = useGetCollaboratorsQuery();
  const collaborators = sortCollaborators(collaboratorsData?.collaborators?.filter(notEmpty) ?? []);

  const sortEmails = (emails: string[]) =>
    sortCollaborators(collaborators.filter(c => emails.includes(c.email))).map(c => c.email);
  const sortedValue = value?.length ? sortEmails(value) : undefined;
  const sortedDefaultValue = defaultValue?.length ? sortEmails(defaultValue) : undefined;

  const valueProps = sortedValue?.length
    ? { value: sortedValue }
    : { defaultValue: sortedDefaultValue };

  const handleAddClick = (_: MouseEvent) => {
    // small wait to let the antd dropdown animations get outta the way
    setTimeout(() => setAddModalVisible(true), 100);
  };

  const handleAddClose = () => {
    setAddModalVisible(false);
  };

  const emptyRender = () => (
    <div>
      <UsergroupAddOutlined style={{ marginRight: '4px' }} />
      <span>No current collaborators</span>
    </div>
  );

  // have to use onMouseDown rather than onClick cause antd
  const dropdownRender = (dropdown: ReactElement) => (
    <div id="collaborator-chooser-dropdown">
      <div onMouseDown={handleAddClick}>
        <AddCollabContainer>
          <PlusOutlined style={{ marginRight: '4px' }} />
          <span>Add Collaborator</span>
        </AddCollabContainer>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      {dropdown}
    </div>
  );

  const isInitialLoading = collaboratorsLoading && !collaboratorsData;

  return (
    <ConfigProvider renderEmpty={emptyRender}>
      <Modal
        title={'Add a Collaborator'}
        open={addModalVisible}
        onCancel={handleAddClose}
        footer={null}
      >
        Please contact <a href="mailto:support@skyspecs.com">support@skyspecs.com</a> to link a new
        collaborator to your account. Once a collaborator relationship has been established, you
        will be able to grant that collaborator account access to whichever Work Orders you'd like.
      </Modal>
      {!isInitialLoading ? (
        <CollaboratorSelect
          mode="multiple"
          popupMatchSelectWidth={false}
          placeholder="Add Collaborators..."
          dropdownRender={dropdownRender}
          filterOption={(input, option) => {
            return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
          }}
          {...valueProps}
          {...props}
        >
          {groupCollaborators(collaborators).map(({ orgName, collaborators: group }) => (
            <Select.OptGroup key={orgName} label={orgName}>
              {group.map(collaborator => {
                const { email } = collaborator;
                return (
                  <Select.Option key={email} value={email}>
                    {formatCollaborator(collaborator)}
                  </Select.Option>
                );
              })}
            </Select.OptGroup>
          ))}
        </CollaboratorSelect>
      ) : null}
    </ConfigProvider>
  );
};
